/* ============================================================
   SPACE / GRID — space-grid.js
   ============================================================
   SPACE mode: infinite 3D tunnel — products fly toward the viewer.
     t = 0.0  → far back (tiny, invisible)
     t = 0.18 → fully opaque, continues growing
     t = 1.0  → past camera (still opaque → wraps back to 0)

   Scroll wheel controls tunnel speed:
     scroll down → products fly faster
     scroll up   → slow / reverse
     stop        → speed decays back to 1×

   GRID mode: standard CSS grid. Grid items are fetched on demand
   from the all-products collection so the initial Space load stays light.
   ============================================================ */

if (!customElements.get('space-grid-section')) {
  customElements.define('space-grid-section', class SpaceGridSection extends HTMLElement {
    static Z_NEAR = 1000;
    static Z_SPACING = 220;
    static FADE_IN_ZONES = 1.5;
    static NEAR_FADE_START = 600;
    static AUTO_ADVANCE = 2;
    static LERP_FACTOR = 0.05;

    connectedCallback() {
      this.spaceList = this.querySelector('[data-space-list]');
      this.gridList = this.querySelector('[data-grid-list]');
      this.spaceItems = Array.from(this.spaceList?.querySelectorAll('.sgv-item') || []);
      this.gridItems = Array.from(this.gridList?.querySelectorAll('.sgv-item:not(.sgv-promo)') || []);
      this.gridPromos = this._readGridPromos();
      this.toggle = this.querySelector('.sgv-toggle');
      this.gridPanel = this.querySelector('.sgv-grid-panel');
      this.primaryFilterButtons = Array.from(this.querySelectorAll('[data-primary-filter]'));
      this.secondaryFilterButtons = Array.from(this.querySelectorAll('[data-secondary-filter]'));
      this.secondaryFilterRow = this.querySelector('.sgv-grid-filters');
      this.stockToggle = this.querySelector('[data-stock-toggle]');
      this.sortButtons = Array.from(this.querySelectorAll('[data-sort-option]'));
      this.colorButtons = Array.from(this.querySelectorAll('[data-color-filter]'));
      this.filtersDrawer = this.querySelector('[data-filters-drawer]');
      this.filtersTrigger = this.querySelector('[data-filters-open]');
      this.filtersCount = this.querySelector('[data-filters-count]');
      this.filtersTriggerCount = this.querySelector('[data-filters-trigger-count]');
      this.gridItemsDefaultOrder = this.gridItems.slice();
      this.gridCount = this.querySelector('[data-grid-count]');
      this.intro = this.querySelector('[data-grid-intro]');
      this.introTitle = this.querySelector('[data-grid-intro-title]');
      this.introText = this.querySelector('[data-grid-intro-text]');
      this.introToggle = this.querySelector('[data-grid-intro-toggle]');
      this.activeTooltipItem = null;
      this.gridFeedUrl = this.dataset.gridFeedUrl || '';
      this.colorSource = this.dataset.colorSource || 'tag';
      this.gridLoaded = this.gridItems.length > 0;
      this.gridLoadingPromise = null;
      this._gridPrefetched = this.gridLoaded;
      this._spaceProductPool = this._readSpaceProductPool();

      if (!this.spaceList || !this.spaceItems.length || !this.toggle) return;

      this._hardReset();

      this._scrollZ = 0;
      this._displayZ = 0;

      this.initialState = this._readInitialState();
      this.currentView = this.initialState.view;
      this.dataset.view = this.currentView;
      this._syncToggleButtons();
      this._syncItemAccessibility();
      this._hydrateGridState(this.initialState);
      this._updateGridIntro();
      this._persistView(this.currentView);
      this._syncBodyViewClass(this.currentView);
      this._syncViewContainers();
      document.dispatchEvent(new CustomEvent('sgv:viewchange', { detail: { view: this.currentView } }));

      this._waitForGSAP(() => this._init());
      this._scheduleGridPrefetch();

      this._resizeHandler = () => {
        if (this.currentView !== 'space') {
          // # Column count changes at the mobile breakpoint, so the banner row moves
          this._placeGridPromos();
          // # A narrower panel rewraps the intro, which can add or drop a line
          this._syncIntroToggleVisibility();
          return;
        }
        this._assignItemData();
      };
      window.addEventListener('resize', this._resizeHandler);

      this._pageHideHandler = () => {
        this._stopTunnel();
        this._unlockScroll();
      };
      this._pageShowHandler = (event) => {
        if (!event.persisted) return;
        this._hardReset();
        this._scrollZ = 0;
        this._displayZ = 0;
        this._syncViewContainers();
        this._waitForGSAP(() => this._init());
      };
      window.addEventListener('pagehide', this._pageHideHandler);
      window.addEventListener('pageshow', this._pageShowHandler);
    }

    disconnectedCallback() {
      this._stopTunnel();
      this._unlockScroll();
      if (this._wheelHandler) window.removeEventListener('wheel', this._wheelHandler);
      if (this._touchStartHandler) window.removeEventListener('touchstart', this._touchStartHandler);
      if (this._touchMoveHandler) window.removeEventListener('touchmove', this._touchMoveHandler);
      if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
      if (this._pageHideHandler) window.removeEventListener('pagehide', this._pageHideHandler);
      if (this._pageShowHandler) window.removeEventListener('pageshow', this._pageShowHandler);
      if (this._filtersKeydownHandler) document.removeEventListener('keydown', this._filtersKeydownHandler);
    }

    _hardReset() {
      this._stopTunnel();
      this._unlockScroll();
      this.spaceItems.forEach((item) => {
        item.style.cssText = '';
        item.hidden = false;
      });
      if (this.spaceList) this.spaceList.style.cssText = '';
      if (this.gridList) this.gridList.style.cssText = '';
    }

    _waitForGSAP(cb) {
      if (window.gsap && window.Flip) {
        cb();
        return;
      }

      let attempts = 0;
      const id = setInterval(() => {
        if (window.gsap && window.Flip) {
          clearInterval(id);
          cb();
        } else if (++attempts > 100) {
          clearInterval(id);
        }
      }, 50);
    }

    _init() {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this._syncIntroToggleVisibility());
      }

      this._bindSpaceInteractions();
      this._bindToggle();
      this._bindGridControls();

      if (this.currentView === 'space') {
        this._prepareSpaceView({ randomize: true });
        return;
      }

      this._unlockScroll();
      this._ensureGridItemsLoaded().then(() => {
        if (this.currentView !== 'grid') return;
        this._showGridView();
        this._applyGridFilters();
      });
    }

    _syncViewContainers() {
      if (!this.spaceList || !this.gridList) return;
      const isGrid = this.currentView === 'grid';
      this.spaceList.hidden = isGrid;
      this.gridList.hidden = !isGrid;
    }

    _prepareSpaceView({ randomize = false } = {}) {
      if (randomize) this._primeSpaceItems();
      this._spaceSequence = null;
      this._syncViewContainers();
      this._assignItemData();
      this._lockScroll();
      this._bindWheel();
      this._bindTouch();
      this._startTunnel();
    }

    _showGridView() {
      this._syncViewContainers();
      this._unlockScroll();
      this._applyGridFilters();
    }

    _scheduleGridPrefetch() {
      if (this._gridPrefetched || !this.gridFeedUrl) return;

      const prefetch = () => {
        this._gridPrefetched = true;
        this._ensureGridItemsLoaded();
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => prefetch(), { timeout: 1200 });
        return;
      }

      window.setTimeout(prefetch, 700);
    }

    _applyMobileLimit() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const limit = isMobile ? (parseInt(this.dataset.mobileLimit, 10) || this.spaceItems.length) : this.spaceItems.length;
      this.spaceItems.forEach((item, index) => {
        item.style.display = index < limit ? '' : 'none';
      });
      return isMobile ? this.spaceItems.slice(0, limit) : this.spaceItems;
    }

    _assignItemData() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      this._tunnelItems = this._applyMobileLimit();
      this._ensureSpaceSequence();

      const mediaMetrics = this._tunnelItems
        .map((item) => this._getMediaMetric(item))
        .filter((metric) => metric > 0);
      const metricMin = mediaMetrics.length ? Math.min(...mediaMetrics) : 0;
      const metricMax = mediaMetrics.length ? Math.max(...mediaMetrics) : 0;

      if (isMobile) {
        const slots = this._getMobileGridSlots();
        this._tunnelItems.forEach((item, index) => {
          const sequenceIndex = this._spaceSequence[index] ?? index;
          const slot = slots[sequenceIndex % slots.length];
          const width = this._getItemWidth(item, slot.baseSize, metricMin, metricMax);
          item.style.left = `${slot.x}%`;
          item.style.top = `${slot.y}%`;
          item.style.width = `${width}px`;
          item._zIdx = sequenceIndex;
          item._prevZ = null;
        });
      } else {
        const sizes = [130, 190, 250];
        const layout = this._resolveSpaceLayout({ isMobile: false, sizes, metricMin, metricMax });
        this._tunnelItems.forEach((item, index) => {
          const sequenceIndex = this._spaceSequence[index] ?? index;
          const placement = layout[index];
          item.style.left = `${placement.position.x}%`;
          item.style.top = `${placement.position.y}%`;
          item.style.width = `${placement.width}px`;
          item._zIdx = sequenceIndex;
          item._prevZ = null;
        });
      }
    }

    _getMobileGridSlots() {
      return [
        { x: 25, y: 27, baseSize: 108 },
        { x: 75, y: 27, baseSize: 80 },
        { x: 25, y: 50, baseSize: 118 },
        { x: 75, y: 50, baseSize: 62 },
        { x: 25, y: 73, baseSize: 105 },
        { x: 75, y: 73, baseSize: 70 }
      ];
    }

    _getDesktopGridSlots() {
      return [
        { x: 12.5, y: 10, baseSize: 172 },
        { x: 37.5, y: 10, baseSize: 152 },
        { x: 62.5, y: 10, baseSize: 142 },
        { x: 87.5, y: 10, baseSize: 162 },
        { x: 12.5, y: 50, baseSize: 192 },
        { x: 37.5, y: 50, baseSize: 162 },
        { x: 62.5, y: 50, baseSize: 172 },
        { x: 87.5, y: 50, baseSize: 158 },
        { x: 12.5, y: 90, baseSize: 172 },
        { x: 37.5, y: 90, baseSize: 148 },
        { x: 62.5, y: 90, baseSize: 158 },
        { x: 87.5, y: 90, baseSize: 172 }
      ];
    }

    _ensureSpaceSequence() {
      if (this._spaceSequence && this._spaceSequence.length === this._tunnelItems.length) return;

      this._spaceSequence = Array.from({ length: this._tunnelItems.length }, (_, index) => index);

      for (let index = this._spaceSequence.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [this._spaceSequence[index], this._spaceSequence[swapIndex]] = [this._spaceSequence[swapIndex], this._spaceSequence[index]];
      }
    }

    _readSpaceProductPool() {
      const script = this.querySelector('[data-space-product-pool]');
      if (!script) return this.spaceItems.map((item) => this._snapshotProductItem(item));

      try {
        const pool = JSON.parse(script.textContent || '[]');
        return Array.isArray(pool) && pool.length ? pool : this.spaceItems.map((item) => this._snapshotProductItem(item));
      } catch (error) {
        return this.spaceItems.map((item) => this._snapshotProductItem(item));
      }
    }

    _snapshotProductItem(item) {
      return {
        html: item.innerHTML,
        productAvailable: item.dataset.productAvailable || 'false',
        productCollections: item.dataset.productCollections || '',
        productUrl: item.dataset.productUrl || '',
        productTitle: item.dataset.productTitle || '',
        productPrice: item.dataset.productPrice || '',
        productMediaWidth: item.dataset.productMediaWidth || '0',
        productMediaHeight: item.dataset.productMediaHeight || '0',
        productAspectRatio: item.dataset.productAspectRatio || '1',
        ariaLabel: item.getAttribute('aria-label') || ''
      };
    }

    _primeSpaceItems() {
      if (!this._spaceProductPool?.length) return;
      const pool = [...this._spaceProductPool];

      for (let index = pool.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [pool[index], pool[swapIndex]] = [pool[swapIndex], pool[index]];
      }

      this.spaceItems.forEach((item, index) => {
        const snapshot = pool[index];
        if (snapshot) this._applyProductSnapshot(item, snapshot);
      });
    }

    _applyProductSnapshot(item, snapshot) {
      if (!snapshot) return;

      item.dataset.productAvailable = snapshot.productAvailable || 'false';
      item.dataset.productCollections = snapshot.productCollections || '';
      item.dataset.productUrl = snapshot.productUrl || '';
      item.dataset.productTitle = snapshot.productTitle || '';
      item.dataset.productPrice = snapshot.productPrice || '';
      item.dataset.productMediaWidth = snapshot.productMediaWidth || '0';
      item.dataset.productMediaHeight = snapshot.productMediaHeight || '0';
      item.dataset.productAspectRatio = snapshot.productAspectRatio || '1';
      item.innerHTML = snapshot.html || '';

      if (snapshot.productUrl) {
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'link');
        item.setAttribute('aria-label', snapshot.ariaLabel || `${snapshot.productTitle} ${snapshot.productPrice}`.trim());
      } else {
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        item.removeAttribute('aria-label');
      }
    }

    _randomizeItemProduct(item) {
      if (!this._spaceProductPool || this._spaceProductPool.length <= this._tunnelItems.length) return;

      const activeKeys = new Set(
        (this._tunnelItems || [])
          .filter((activeItem) => activeItem !== item)
          .map((activeItem) => activeItem.dataset.productUrl)
          .filter(Boolean)
      );

      const candidates = this._spaceProductPool.filter((snapshot) => snapshot.productUrl && !activeKeys.has(snapshot.productUrl));
      if (!candidates.length) return;

      const nextSnapshot = candidates[Math.floor(Math.random() * candidates.length)];
      this._applyProductSnapshot(item, nextSnapshot);
    }

    _getDesktopScatterPosition(index) {
      const rawX = 4 + ((index * 37 + 17) % 92);
      const rawY = 6 + ((index * 53 + 11) % 82);

      return {
        x: this._pushAwayFromCenter(rawX, 50, 22, 4, 96),
        y: this._pushAwayFromCenter(rawY, 44, 16, 6, 88)
      };
    }

    _resolveSpaceLayout({ isMobile, sizes, metricMin, metricMax }) {
      const rect = this.spaceList.getBoundingClientRect();
      const viewportWidth = rect.width || window.innerWidth || 1;
      const viewportHeight = rect.height || window.innerHeight || 1;
      const padding = isMobile ? 18 : 28;
      const items = [];
      const placedBoxes = [];

      this._tunnelItems.forEach((item, index) => {
        const sequenceIndex = this._spaceSequence[index] ?? index;
        const preferred = this._getDesktopScatterPosition(sequenceIndex);
        const baseSize = preferred.baseSize || sizes[sequenceIndex % sizes.length];
        const width = this._getItemWidth(item, baseSize, metricMin, metricMax);
        const height = this._getItemHeight(item, width);
        const position = this._findNonOverlappingPosition({
          preferred,
          width,
          height,
          viewportWidth,
          viewportHeight,
          padding,
          placedBoxes,
          isMobile,
          seed: sequenceIndex + index * 17
        });

        placedBoxes.push(this._getPlacementBox(position, width, height, viewportWidth, viewportHeight, padding));
        items.push({ position, width, height });
      });

      return items;
    }

    _findNonOverlappingPosition({ preferred, width, height, viewportWidth, viewportHeight, padding, placedBoxes, isMobile, seed }) {
      const maxAttempts = 52;
      let bestPosition = preferred;
      let bestOverlap = Number.POSITIVE_INFINITY;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const candidate = this._generatePlacementCandidate(preferred, attempt, isMobile, seed);
        const box = this._getPlacementBox(candidate, width, height, viewportWidth, viewportHeight, padding);
        const overlap = this._measurePlacementOverlap(box, placedBoxes);

        if (overlap === 0) return candidate;
        if (overlap < bestOverlap) {
          bestOverlap = overlap;
          bestPosition = candidate;
        }
      }

      return bestPosition;
    }

    _generatePlacementCandidate(preferred, attempt, isMobile, seed) {
      if (attempt === 0) return preferred;

      const amplitudeX = 16;
      const amplitudeY = 12;
      const phase = seed + attempt * 1.61803398875;
      const offsetX = Math.sin(phase) * amplitudeX + Math.cos(phase * 0.7) * amplitudeX * 0.35;
      const offsetY = Math.cos(phase * 1.13) * amplitudeY + Math.sin(phase * 0.5) * amplitudeY * 0.4;

      return {
        ...preferred,
        x: gsap.utils.clamp(4, 96, preferred.x + offsetX),
        y: gsap.utils.clamp(6, 88, preferred.y + offsetY)
      };
    }

    _getPlacementBox(position, width, height, viewportWidth, viewportHeight, padding) {
      const centerX = (position.x / 100) * viewportWidth;
      const centerY = (position.y / 100) * viewportHeight;
      const collisionScale = 0.78;
      const boxWidth = width * collisionScale + padding;
      const boxHeight = height * collisionScale + padding;

      return {
        left: centerX - boxWidth / 2,
        right: centerX + boxWidth / 2,
        top: centerY - boxHeight / 2,
        bottom: centerY + boxHeight / 2
      };
    }

    _measurePlacementOverlap(box, placedBoxes) {
      return placedBoxes.reduce((total, placedBox) => {
        const overlapX = Math.max(0, Math.min(box.right, placedBox.right) - Math.max(box.left, placedBox.left));
        const overlapY = Math.max(0, Math.min(box.bottom, placedBox.bottom) - Math.max(box.top, placedBox.top));
        return total + overlapX * overlapY;
      }, 0);
    }

    _getMobileCompositionSlot(index) {
      const slots = [
        { x: 26, y: 18, baseSize: 155, depth: 0.88, waveAmp: 0 },
        { x: 65, y: 22, baseSize: 122, depth: 0.90, waveAmp: 0 },
        { x: 36, y: 37, baseSize: 90, depth: 0.94, waveAmp: 0 },
        { x: 62, y: 40, baseSize: 108, depth: 0.92, waveAmp: 0 },
        { x: 20, y: 52, baseSize: 165, depth: 0.86, waveAmp: 0 },
        { x: 64, y: 50, baseSize: 68, depth: 0.97, waveAmp: 0 },
        { x: 38, y: 64, baseSize: 72, depth: 0.96, waveAmp: 0 },
        { x: 69, y: 67, baseSize: 128, depth: 0.89, waveAmp: 0 },
        { x: 24, y: 78, baseSize: 115, depth: 0.91, waveAmp: 0 },
        { x: 66, y: 82, baseSize: 88, depth: 0.93, waveAmp: 0 },
        { x: 54, y: 30, baseSize: 78, depth: 0.95, waveAmp: 0 },
        { x: 48, y: 58, baseSize: 82, depth: 0.96, waveAmp: 0 }
      ];
      const slot = slots[index % slots.length];
      const cycle = Math.floor(index / slots.length);
      const jitterX = ((cycle * 11 + index * 5) % 5) - 2;
      const jitterY = ((cycle * 13 + index * 3) % 7) - 3;
      const rawX = gsap.utils.clamp(2, 98, slot.x + jitterX);
      const rawY = gsap.utils.clamp(8, 94, slot.y + jitterY);

      return {
        x: rawX,
        y: rawY,
        baseSize: slot.baseSize,
        depth: slot.depth,
        waveAmp: slot.waveAmp
      };
    }

    _getMediaMetric(item) {
      const width = parseFloat(item.dataset.productMediaWidth) || 0;
      const height = parseFloat(item.dataset.productMediaHeight) || 0;
      if (!width || !height) return 0;
      return Math.sqrt(width * height);
    }

    _pushAwayFromCenter(value, center, radius, min, max) {
      const distance = value - center;
      if (Math.abs(distance) >= radius) return value;

      const direction = distance < 0 ? -1 : 1;
      const pushed = center + direction * radius;
      return gsap.utils.clamp(min, max, pushed);
    }

    _getItemWidth(item, baseSize, metricMin, metricMax) {
      const metric = this._getMediaMetric(item);
      const aspectRatio = parseFloat(item.dataset.productAspectRatio) || 1;
      const normalizedMetric = metricMax > metricMin ? (metric - metricMin) / (metricMax - metricMin) : 0.5;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const sizeScale = isMobile
        ? 0.82 + normalizedMetric * 0.38
        : 0.82 + normalizedMetric * 0.58;
      const aspectScale = gsap.utils.clamp(0.92, 1.14, Math.sqrt(aspectRatio));
      return Math.round(baseSize * sizeScale * aspectScale);
    }

    _getItemHeight(item, width) {
      const aspectRatio = parseFloat(item.dataset.productAspectRatio) || 1;
      const normalizedAspectRatio = gsap.utils.clamp(0.6, 1.8, aspectRatio);
      return Math.round(width / normalizedAspectRatio);
    }

    _startTunnel() {
      if (this._tickerFn) return;
      const C = SpaceGridSection;

      this._tickerFn = () => {
        const items = this._tunnelItems || this.spaceItems;
        const n = items.length;
        const farZ = -(n - 2) * C.Z_SPACING;

        this._scrollZ += C.AUTO_ADVANCE;
        this._displayZ += (this._scrollZ - this._displayZ) * C.LERP_FACTOR;

        items.forEach((item) => {
          const z = gsap.utils.wrap(farZ, C.Z_NEAR, -(item._zIdx) * C.Z_SPACING + this._displayZ + 150);
          const fadeInRange = C.Z_SPACING * C.FADE_IN_ZONES;
          const farOpacity = Math.max(0, Math.min(1, (z - farZ) / fadeInRange));
          const nearOpacity = Math.max(0, Math.min(1, (C.Z_NEAR - z) / (C.Z_NEAR - C.NEAR_FADE_START)));
          const opacity = farOpacity * nearOpacity;

          const prevZ = item._prevZ ?? z;
          if (prevZ > 800 && z < -500) this._randomizeItemProduct(item);
          item._prevZ = z;

          gsap.set(item, { x: 0, y: 0, rotateX: 0, rotateY: 0, z, opacity });
        });
      };

      gsap.ticker.add(this._tickerFn);
    }

    _stopTunnel() {
      if (!this._tickerFn) return;
      gsap.ticker.remove(this._tickerFn);
      this._tickerFn = null;
    }

    _bindWheel() {
      if (this._wheelHandler) return;
      this._wheelHandler = (event) => {
        if (this.currentView !== 'space') return;
        if (event.target.closest('raiffe-header.is-nav-open')) return;
        event.preventDefault();
        this._scrollZ += event.deltaY;
      };
      window.addEventListener('wheel', this._wheelHandler, { passive: false });
    }

    _bindTouch() {
      if (this._touchStartHandler || this._touchMoveHandler) return;
      let touchY0 = null;

      this._touchStartHandler = (event) => {
        if (this.currentView !== 'space') return;
        if (event.target.closest('raiffe-header.is-nav-open')) {
          touchY0 = null;
          return;
        }
        touchY0 = event.touches[0].clientY;
      };

      this._touchMoveHandler = (event) => {
        if (this.currentView !== 'space' || touchY0 === null) return;
        if (event.target.closest('raiffe-header.is-nav-open')) {
          touchY0 = null;
          return;
        }
        event.preventDefault();
        this._scrollZ -= 8 * (event.touches[0].clientY - touchY0);
        touchY0 = event.touches[0].clientY;
      };

      window.addEventListener('touchstart', this._touchStartHandler, { passive: true });
      window.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
    }

    _bindSpaceInteractions() {
      if (this._spaceInteractionsBound) return;
      this._spaceInteractionsBound = true;

      this.spaceItems.forEach((item) => {
        item.addEventListener('mouseenter', () => this._setTooltipItem(item));
        item.addEventListener('mouseleave', () => this._setTooltipItem(null));
        item.addEventListener('focus', () => this._setTooltipItem(item));
        item.addEventListener('blur', () => this._setTooltipItem(null));

        item.addEventListener('click', (event) => {
          if (this.currentView !== 'space') return;
          if (event.target.closest('a, button')) return;
          this._navigateToItem(item);
        });

        item.addEventListener('keydown', (event) => {
          if (this.currentView !== 'space') return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this._navigateToItem(item);
        });
      });
    }

    _setTooltipItem(item) {
      if (this.activeTooltipItem && this.activeTooltipItem !== item) {
        this.activeTooltipItem.classList.remove('is-tooltip-visible');
      }

      this.activeTooltipItem = item;
      if (item) item.classList.add('is-tooltip-visible');
    }

    _navigateToItem(item) {
      const url = item.dataset.productUrl;
      if (!url) return;
      window.location.href = url;
    }

    _lockScroll() {
      document.documentElement.style.overflow = 'hidden';
    }

    _unlockScroll() {
      document.documentElement.style.overflow = '';
    }

    _bindToggle() {
      if (this._toggleBound) return;
      this._toggleBound = true;

      const handleToggle = (button) => {
        if (!button || button.dataset.view === this.currentView) return;
        this._switchView(button.dataset.view);
      };

      this.toggle.addEventListener('pointerup', (event) => {
        const button = event.target.closest('[data-view]');
        if (!button) return;
        event.preventDefault();
        handleToggle(button);
      });

      this.toggle.addEventListener('click', (event) => {
        if (event.detail !== 0) return;
        const button = event.target.closest('[data-view]');
        handleToggle(button);
      });
    }

    _bindGridControls() {
      if (this._gridControlsBound) return;
      this._gridControlsBound = true;

      this.primaryFilterButtons.forEach((button) => {
        button.addEventListener('click', async () => {
          this.activePrimaryFilter = this.activePrimaryFilter === button.dataset.primaryFilter ? '' : button.dataset.primaryFilter;
          this._syncSecondaryFilterScope();
          this._syncFilterButtons();
          await this._ensureGridItemsLoaded();
          this._applyGridFilters();
        });
      });

      this.secondaryFilterButtons.forEach((button) => {
        button.addEventListener('click', async () => {
          this.activeSecondaryFilter = this.activeSecondaryFilter === button.dataset.secondaryFilter ? '' : button.dataset.secondaryFilter;
          this._syncFilterButtons();
          await this._ensureGridItemsLoaded();
          this._applyGridFilters();
        });
      });

      if (this.stockToggle) {
        this.stockToggle.addEventListener('click', () => {
          this.pendingStockOnly = !this.pendingStockOnly;
          this._syncFilterDrawer();
        });
      }

      this.sortButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.sortOption;
          this.pendingSort = this.pendingSort === value ? '' : value;
          this._syncFilterDrawer();
        });
      });

      this.colorButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.colorFilter;
          if (this.pendingColors.has(value)) {
            this.pendingColors.delete(value);
          } else {
            this.pendingColors.add(value);
          }
          this._syncFilterDrawer();
        });
      });

      if (this.filtersTrigger) {
        this.filtersTrigger.addEventListener('click', () => this._openFilters());
      }

      if (this.filtersDrawer) {
        this.filtersDrawer.querySelectorAll('[data-filters-close]').forEach((button) => {
          button.addEventListener('click', () => this._closeFilters());
        });

        const applyButton = this.filtersDrawer.querySelector('[data-filters-apply]');
        if (applyButton) {
          applyButton.addEventListener('click', async () => {
            this._commitPendingFilters();
            this._closeFilters();
            await this._ensureGridItemsLoaded();
            this._applyGridFilters();
          });
        }

        const clearButton = this.filtersDrawer.querySelector('[data-filters-clear]');
        if (clearButton) {
          clearButton.addEventListener('click', () => {
            this.pendingSort = '';
            this.pendingStockOnly = false;
            this.pendingColors = new Set();
            this._syncFilterDrawer();
          });
        }

        this._filtersKeydownHandler = (event) => {
          if (event.key !== 'Escape' || this.filtersDrawer.hidden) return;
          this._closeFilters();
        };
        document.addEventListener('keydown', this._filtersKeydownHandler);
      }

      if (this.introToggle && this.intro) {
        this.introToggle.addEventListener('click', () => {
          const expanded = this.intro.classList.toggle('is-expanded');
          this.introToggle.setAttribute('aria-expanded', String(expanded));
        });
      }
    }

    _openFilters() {
      if (!this.filtersDrawer) return;

      // # Staged copy of the committed state - nothing hits the grid until Apply
      this.pendingSort = this.activeSort;
      this.pendingStockOnly = this.stockOnly;
      this.pendingColors = new Set(this.activeColors);
      this._syncFilterDrawer();

      this.filtersDrawer.hidden = false;
      document.body.classList.add('sgv-filters-open');
      this._lockScroll();
      this._ensureGridItemsLoaded();
      requestAnimationFrame(() => this.filtersDrawer.classList.add('is-open'));

      if (this.filtersTrigger) {
        this.filtersTrigger.setAttribute('aria-expanded', 'true');
        this.filtersTrigger.classList.add('is-active');
      }
    }

    _closeFilters() {
      if (!this.filtersDrawer || this.filtersDrawer.hidden) return;

      this.filtersDrawer.classList.remove('is-open');
      document.body.classList.remove('sgv-filters-open');

      if (this.filtersTrigger) {
        this.filtersTrigger.setAttribute('aria-expanded', 'false');
        this.filtersTrigger.classList.remove('is-active');
        this.filtersTrigger.focus();
      }

      if (this.currentView !== 'space') this._unlockScroll();

      window.setTimeout(() => {
        if (this.filtersDrawer.classList.contains('is-open')) return;
        this.filtersDrawer.hidden = true;
      }, 280);
    }

    _commitPendingFilters() {
      this.activeSort = this.pendingSort;
      this.stockOnly = this.pendingStockOnly;
      this.activeColors = new Set(this.pendingColors);
      this._syncFilterTriggerCount();
    }

    _activeFilterCount(sort, stockOnly, colors) {
      return (sort ? 1 : 0) + (stockOnly ? 1 : 0) + colors.size;
    }

    _syncFilterDrawer() {
      this.sortButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.sortOption === this.pendingSort));
      });

      this.colorButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(this.pendingColors.has(button.dataset.colorFilter)));
      });

      if (this.stockToggle) {
        this.stockToggle.setAttribute('aria-pressed', String(this.pendingStockOnly));
      }

      if (this.filtersCount) {
        this.filtersCount.textContent = '(' + this._activeFilterCount(this.pendingSort, this.pendingStockOnly, this.pendingColors) + ')';
      }
    }

    _syncFilterTriggerCount() {
      if (!this.filtersTriggerCount) return;

      const count = this._activeFilterCount(this.activeSort, this.stockOnly, this.activeColors);
      this.filtersTriggerCount.textContent = '(' + count + ')';
      this.filtersTriggerCount.hidden = count === 0;
    }

    _switchView(targetView) {
      gsap.killTweensOf(this.spaceItems);
      gsap.killTweensOf(this.gridItems);

      this.currentView = targetView;
      this.dataset.view = targetView;
      this._syncToggleButtons();
      this._syncItemAccessibility();
      this._persistView(targetView);

      if (targetView === 'space') {
        this._syncBodyViewClass('space');
      } else {
        this._setBodyTransitionState('grid');
      }

      document.dispatchEvent(new CustomEvent('sgv:viewchange', { detail: { view: targetView } }));

      if (targetView === 'grid') {
        this._toGrid();
      } else {
        this._toSpace();
      }
    }

    _toGrid() {
      this._stopTunnel();
      this._showGridLoadingState();

      gsap.to(this.spaceItems, {
        opacity: 0,
        scale: 0.95,
        duration: 0.24,
        stagger: 0.03,
        ease: 'power2.in',
        onComplete: async () => {
          if (this.currentView !== 'grid') return;

          this.spaceItems.forEach((item) => {
            gsap.set(item, { clearProps: 'all' });
            item.style.cssText = '';
          });

          this._unlockScroll();
          await this._ensureGridItemsLoaded();
          if (this.currentView !== 'grid') return;

          this._showGridView();
          const visibleItems = this._visibleGridNodes();
          gsap.set(this.gridPanel, { opacity: 0, y: 18, filter: 'blur(10px)' });

          gsap.timeline({
            defaults: { ease: 'power2.out' },
            onComplete: () => {
              if (this.currentView !== 'grid') return;
              this._syncBodyViewClass('grid');
              this._clearGridLoadingState();
              gsap.set(this.gridPanel, { clearProps: 'opacity,transform,filter' });
              visibleItems.forEach((item) => gsap.set(item, { clearProps: 'all' }));
            }
          })
            .to(this.gridPanel, {
              opacity: 1,
              y: 0,
              filter: 'blur(0px)',
              duration: 0.28
            })
            .from(visibleItems, {
              opacity: 0,
              y: 22,
              scale: 0.98,
              duration: 0.32,
              stagger: 0.02
            }, 0.04);
        }
      });
    }

    _toSpace() {
      const activeGridItems = this._visibleGridNodes();
      gsap.killTweensOf(this.gridPanel);
      const finalize = () => {
        if (this.currentView !== 'space') return;

        activeGridItems.forEach((item) => {
          gsap.set(item, { clearProps: 'all' });
        });

        this._scrollZ = 0;
        this._displayZ = 0;
        this._prepareSpaceView({ randomize: true });
        this._syncBodyViewClass('space');
        gsap.set(this.gridPanel, { clearProps: 'opacity,transform,filter' });
      };

      if (!activeGridItems.length) {
        finalize();
        return;
      }

      gsap.timeline({
        defaults: { ease: 'power1.inOut' },
        onComplete: finalize
      })
        .to(activeGridItems, {
          opacity: 0,
          y: -12,
          duration: 0.2,
          stagger: 0.008
        }, 0)
        .to(this.gridPanel, {
          opacity: 0.35,
          duration: 0.18
        }, 0);
    }

    _showGridLoadingState() {
      if (!this.gridPanel) return;

      gsap.killTweensOf(this.gridPanel);
      gsap.to(this.gridPanel, {
        opacity: 0.4,
        duration: 0.18,
        ease: 'power1.out'
      });
    }

    _clearGridLoadingState() {
      if (!this.gridPanel) return;

      gsap.killTweensOf(this.gridPanel);
      gsap.to(this.gridPanel, {
        opacity: 1,
        duration: 0.16,
        ease: 'power1.out'
      });
    }

    _syncToggleButtons() {
      this.toggle.querySelectorAll('[data-view]').forEach((button) => {
        const active = button.dataset.view === this.currentView;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    _syncItemAccessibility() {
      this.spaceItems.forEach((item) => {
        const url = item.dataset.productUrl;
        if (!url) return;

        if (this.currentView === 'space') {
          item.setAttribute('tabindex', '0');
          item.setAttribute('role', 'link');
        } else {
          item.removeAttribute('tabindex');
          item.removeAttribute('role');
          item.classList.remove('is-tooltip-visible');
        }
      });
    }

    _readInitialState() {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('sgv-view') === 'grid' ? 'grid' : 'space';
      return {
        view,
        primaryFilter: params.get('sgv-primary') || '',
        secondaryFilter: params.get('sgv-filter') || ''
      };
    }

    _hydrateGridState(initialState = {}) {
      const activePrimaryButton = this.primaryFilterButtons.find((button) => button.classList.contains('is-active'));
      const activeSecondaryButton = this.secondaryFilterButtons.find((button) => button.classList.contains('is-active'));
      const requestedPrimaryButton = this.primaryFilterButtons.find((button) => button.dataset.primaryFilter === initialState.primaryFilter);
      const requestedSecondaryButton = this.secondaryFilterButtons.find((button) => (
        button.dataset.secondaryFilter === initialState.secondaryFilter ||
        button.dataset.secondaryFilterKey === initialState.secondaryFilter
      ));

      this.activePrimaryFilter = requestedPrimaryButton
        ? requestedPrimaryButton.dataset.primaryFilter
        : (activePrimaryButton ? activePrimaryButton.dataset.primaryFilter : '');
      this.activeSecondaryFilter = requestedSecondaryButton
        ? requestedSecondaryButton.dataset.secondaryFilter
        : (activeSecondaryButton ? activeSecondaryButton.dataset.secondaryFilter : '');
      this.activeSort = '';
      this.stockOnly = false;
      this.activeColors = new Set();
      this.pendingSort = '';
      this.pendingStockOnly = false;
      this.pendingColors = new Set();
      this._syncSecondaryFilterScope();
      this._syncFilterButtons();
      this._syncFilterDrawer();
      this._syncFilterTriggerCount();
    }

    // # A chip can be tied to one primary tab, so switching tabs swaps the chip row.
    // # Chips with no parent stay put, and so does every chip while no tab is active.
    _syncSecondaryFilterScope() {
      this.secondaryFilterButtons.forEach((button) => {
        const parent = button.dataset.filterParent || '';
        const inScope = !parent || !this.activePrimaryFilter || parent === this.activePrimaryFilter;
        button.hidden = !inScope;
      });

      // # Two chips or fewer read as stranded when pushed to the edges
      if (this.secondaryFilterRow) {
        const visibleChips = this.secondaryFilterButtons.filter((button) => !button.hidden).length;
        this.secondaryFilterRow.classList.toggle('is-sparse', visibleChips <= 2);
      }

      if (!this.activeSecondaryFilter) return;

      // # The active chip may have just left the row — drop the filter with it
      const activeButton = this.secondaryFilterButtons.find(
        (button) => button.dataset.secondaryFilter === this.activeSecondaryFilter
      );
      if (!activeButton || activeButton.hidden) this.activeSecondaryFilter = '';
    }

    _syncFilterButtons() {
      this.primaryFilterButtons.forEach((button) => {
        const active = button.dataset.primaryFilter === this.activePrimaryFilter && this.activePrimaryFilter !== '';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });

      this.secondaryFilterButtons.forEach((button) => {
        const active = button.dataset.secondaryFilter === this.activeSecondaryFilter && this.activeSecondaryFilter !== '';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    async _ensureGridItemsLoaded() {
      if (this.gridLoaded || !this.gridFeedUrl || !this.gridList) return;
      if (this.gridLoadingPromise) {
        await this.gridLoadingPromise;
        return;
      }

      this.gridLoadingPromise = fetch(this.gridFeedUrl)
        .then((response) => response.text())
        .then((markup) => {
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const feedList = doc.querySelector('[data-space-grid-feed-list]');
          if (!feedList) return;

          this.gridList.innerHTML = feedList.innerHTML;
          this.gridItems = Array.from(this.gridList.querySelectorAll('.sgv-item:not(.sgv-promo)'));
          this.gridItemsDefaultOrder = this.gridItems.slice();
          this.gridLoaded = this.gridItems.length > 0;
          this.gridPromos.forEach((promo) => this.gridList.appendChild(promo));
        })
        .catch(() => {
          this.gridLoaded = false;
        })
        .finally(() => {
          this.gridLoadingPromise = null;
        });

      await this.gridLoadingPromise;
    }

    _readGridPromos() {
      const template = this.querySelector('[data-grid-promos]');
      if (!template) return [];
      return Array.from(template.content.querySelectorAll('.sgv-promo')).map((node) => node.cloneNode(true));
    }

    // # Cells per grid row — mobile is always 2 columns, desktop follows --sgv-cols
    _gridColumns() {
      if (window.matchMedia('(max-width: 749px)').matches) return 2;
      const columns = parseInt(getComputedStyle(this).getPropertyValue('--sgv-cols'), 10);
      return Number.isFinite(columns) && columns > 0 ? columns : 4;
    }

    // # Flow index of the cell the banner starts at, counting banners as 2 cells
    _promoCellIndex(promo, columns) {
      const row = Math.max(1, parseInt(promo.dataset.promoRow, 10) || 1);
      const start = (row - 1) * columns;
      return promo.dataset.promoAlign === 'end' ? start + Math.max(0, columns - 2) : start;
    }

    // # Banners sit at a fixed cell, so their slot is measured against visible
    // # products only — filtering reflows them into the right row again.
    _placeGridPromos() {
      if (!this.gridList || !this.gridPromos.length) return;

      const columns = this._gridColumns();
      const visibleProducts = this.gridItems.filter((item) => !item.hidden);
      let cellsTakenByPromos = 0;

      this.gridPromos
        .map((promo) => ({ promo, cell: this._promoCellIndex(promo, columns) }))
        .sort((a, b) => a.cell - b.cell)
        .forEach(({ promo, cell }) => {
          const productsBefore = Math.max(0, cell - cellsTakenByPromos);

          if (!visibleProducts.length || productsBefore > visibleProducts.length) {
            promo.hidden = true;
            return;
          }

          promo.hidden = false;
          const anchor = visibleProducts[productsBefore];
          if (anchor) {
            this.gridList.insertBefore(promo, anchor);
          } else {
            this.gridList.appendChild(promo);
          }
          cellsTakenByPromos += 2;
        });
    }

    _visibleGridNodes() {
      if (!this.gridList) return [];
      return Array.from(this.gridList.querySelectorAll('.sgv-item:not([hidden])'));
    }

    _applyGridFilters() {
      if (!this.gridItems.length) {
        this.gridPromos.forEach((promo) => { promo.hidden = true; });
        if (this.gridCount) this.gridCount.textContent = '0';
        this._updateGridIntro();
        return;
      }

      this._sortGridItems();

      let visibleCount = 0;

      this.gridItems.forEach((item) => {
        const matchesPrimary = !this.activePrimaryFilter || this._itemHasCollection(item, this.activePrimaryFilter);
        const matchesSecondary = !this.activeSecondaryFilter || this._itemHasCollection(item, this.activeSecondaryFilter);
        const matchesStock = !this.stockOnly || item.dataset.productAvailable === 'true';
        const matchesColor = !this.activeColors.size || this._itemMatchesColor(item);
        const visible = matchesPrimary && matchesSecondary && matchesStock && matchesColor;

        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this._placeGridPromos();
      if (this.gridCount) this.gridCount.textContent = String(visibleCount);
      this._updateGridIntro();
    }

    _updateGridIntro() {
      if (!this.intro) return;

      const defaultTitle = this.intro.dataset.defaultIntroTitle || '';
      const defaultText = this.intro.dataset.defaultIntroText || '';
      const activeSecondaryButton = this.secondaryFilterButtons.find((button) => button.dataset.secondaryFilter === this.activeSecondaryFilter);

      const title = activeSecondaryButton?.dataset.introTitle || defaultTitle;
      const text = activeSecondaryButton?.dataset.introText || defaultText;

      this._setGridIntroTitle(title || defaultTitle);
      this._setGridIntroText(text || defaultText);
      this._resetGridIntroToggle();
    }

    _setGridIntroTitle(title) {
      if (!this.introTitle && !title) return;

      if (!this.introTitle && title) {
        this.introTitle = document.createElement('h2');
        this.introTitle.className = 'sgv-grid-intro__title';
        this.introTitle.setAttribute('data-grid-intro-title', '');
        this.intro.prepend(this.introTitle);
      }

      if (!this.introTitle) return;

      if (title) {
        this.introTitle.textContent = title;
        this.introTitle.hidden = false;
      } else {
        this.introTitle.textContent = '';
        this.introTitle.hidden = true;
      }
    }

    _setGridIntroText(text) {
      if (!this.introText && !text) return;

      if (!this.introText && text) {
        this.introText = document.createElement('div');
        this.introText.className = 'sgv-grid-intro__text rte';
        this.introText.setAttribute('data-grid-intro-text', '');

        if (this.introToggle) {
          this.intro.insertBefore(this.introText, this.introToggle);
        } else {
          this.intro.append(this.introText);
        }
      }

      if (!this.introText) return;

      if (text) {
        this.introText.innerHTML = this._formatIntroText(text);
        this.introText.hidden = false;
      } else {
        this.introText.innerHTML = '';
        this.introText.hidden = true;
      }
    }

    _resetGridIntroToggle() {
      if (!this.intro || !this.introToggle) return;

      this.intro.classList.remove('is-expanded');
      this.introToggle.setAttribute('aria-expanded', 'false');
      this._syncIntroToggleVisibility();
    }

    // # "Read more" only earns its place when the clamp actually cuts the text off
    _syncIntroToggleVisibility() {
      if (!this.intro || !this.introToggle) return;

      if (!this.introText || this.introText.hidden) {
        this.introToggle.hidden = true;
        return;
      }

      if (this.intro.classList.contains('is-expanded')) {
        this.introToggle.hidden = false;
        return;
      }

      // # Measure clamped: a stale is-fits would report the full height and lie
      this.intro.classList.remove('is-fits');

      // # Collapsed height vs. full height — a pixel of slack for rounding
      const overflows = this.introText.scrollHeight - this.introText.clientHeight > 1;

      this.intro.classList.toggle('is-fits', !overflows);
      this.introToggle.hidden = !overflows;
    }

    _formatIntroText(text) {
      return this._escapeHtml(text).replace(/\r\n|\r|\n/g, '<br>');
    }

    _escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    // # Reorders the products in place; the banners are re-anchored right after
    _sortGridItems() {
      if (!this.gridList || !this.gridItemsDefaultOrder.length) return;

      const sorted = this.gridItemsDefaultOrder.slice();

      if (this.activeSort === 'price-ascending') {
        sorted.sort((a, b) => this._itemPrice(a) - this._itemPrice(b));
      } else if (this.activeSort === 'price-descending') {
        sorted.sort((a, b) => this._itemPrice(b) - this._itemPrice(a));
      } else if (this.activeSort === 'newest') {
        sorted.sort((a, b) => this._itemCreated(b) - this._itemCreated(a));
      }

      this.gridItems = sorted;

      const fragment = document.createDocumentFragment();
      sorted.forEach((item) => fragment.appendChild(item));
      this.gridList.appendChild(fragment);
    }

    _itemPrice(item) {
      const price = parseInt(item.dataset.productPriceCents, 10);
      return Number.isFinite(price) ? price : 0;
    }

    _itemCreated(item) {
      const created = parseInt(item.dataset.productCreated, 10);
      return Number.isFinite(created) ? created : 0;
    }

    _itemMatchesColor(item) {
      const values = new Set(this._itemColorValues(item));

      for (const color of this.activeColors) {
        if (values.has(color)) return true;
      }
      return false;
    }

    // # Tags and the Shopify category metafield are separate sources — the section
    // # setting decides which one (or both) a color chip is matched against
    _itemColorValues(item) {
      const split = (value) => (value ? value.split(',') : []);
      const tagValues = split(item.dataset.productTags).concat(split(item.dataset.productCollections));
      const metafieldValues = split(item.dataset.productColors);

      let values = tagValues;
      if (this.colorSource === 'metafield') {
        values = metafieldValues;
      } else if (this.colorSource === 'both') {
        values = tagValues.concat(metafieldValues);
      }

      return values.map((value) => value.trim().toLowerCase()).filter(Boolean);
    }

    _itemHasCollection(item, handle) {
      const handles = item.dataset.productCollections ? item.dataset.productCollections.split(',') : [];
      return handles.includes(handle);
    }

    _persistView(view) {
      try {
        localStorage.setItem('sgv-view', view);
      } catch (error) {}
    }

    _syncBodyViewClass(view) {
      document.documentElement.classList.toggle('sgv-view-space', view === 'space');
      document.documentElement.classList.toggle('sgv-view-grid', view === 'grid');
      document.body.classList.toggle('sgv-view-space', view === 'space');
      document.body.classList.toggle('sgv-view-grid', view === 'grid');
      document.body.classList.remove('sgv-transitioning-to-grid');
    }

    _setBodyTransitionState(target) {
      document.body.classList.toggle('sgv-transitioning-to-grid', target === 'grid');
    }
  });
}

document.addEventListener('shopify:section:load', (event) => {
  const el = event.target.querySelector('space-grid-section');
  if (el) el.connectedCallback();
});
